/**
 * Derive a stable dedup code from a browser error's stack trace.
 * Used for unhandled crashes (ErrorBoundary, window.onerror,
 * unhandledrejection). Explicit logError calls provide their own codes.
 *
 * Handles all major browser stack formats:
 * - Chrome/Edge (V8):   "    at funcName (file:line:col)"
 * - Firefox:            "funcName@file:line:col"
 * - Safari:             "funcName@file:line:col" (may omit col)
 *
 * Skips frames from node_modules, react-dom, webpack internals,
 * and chunk- prefixed files.
 *
 * Produces: {ErrorType}@{chunkFile}:{line}:{col}
 * Example: TypeError@index-a1b2c3d4.js:1:284573
 *
 * @param {Error|string} error
 * @returns {string} dedup code
 */

const skipPatterns = ['node_modules', 'react-dom', 'webpack', 'chunk-'];

const shouldSkipFrame = (frameLine) => skipPatterns.some(
  (pattern) => frameLine.includes(pattern),
);

// Extract filename from a URL or path (last segment before query/hash)
const extractFilename = (filePath) => {
  // Remove query string and hash
  const clean = filePath.split('?')[0].split('#')[0];
  const segments = clean.split('/');
  return segments[segments.length - 1] || clean;
};

// Try Chrome/V8 format: "    at funcName (file:line:col)" or "    at file:line:col"
const parseV8Frame = (line) => {
  const parenMatch = line.match(/\(([^)]+):(\d+):(\d+)\)\s*$/); // NOSONAR client-side, bounded/trusted input
  if (parenMatch) {
    return { file: parenMatch[1], line: parenMatch[2], col: parenMatch[3] };
  }
  const directMatch = line.match(/at\s+(\S+):(\d+):(\d+)\s*$/);
  if (directMatch) {
    return { file: directMatch[1], line: directMatch[2], col: directMatch[3] };
  }
  return null;
};

// Try Firefox/Safari format: "funcName@file:line:col" or "func@file:line"
// Safari may omit column. Lines like "@file:line:col" (anonymous) are valid.
const parseGeckoFrame = (line) => {
  // Match: optional_func@url:line:col or optional_func@url:line
  const match = line.match(/@(.+):(\d+):(\d+)\s*$/); // NOSONAR client-side, bounded/trusted input
  if (match) {
    return { file: match[1], line: match[2], col: match[3] };
  }
  // Safari sometimes omits column
  const noColMatch = line.match(/@(.+):(\d+)\s*$/); // NOSONAR client-side, bounded/trusted input
  if (noColMatch) {
    return { file: noColMatch[1], line: noColMatch[2], col: '0' };
  }
  return null;
};

const deriveErrorCode = (error) => {
  if (!error) return 'unknown';

  // Handle non-Error values (strings, objects thrown as errors)
  if (typeof error === 'string') return 'Error@unknown';

  const errorType = error.name || error.constructor?.name || 'Error';
  const stack = error.stack || '';

  const lines = stack.split('\n');

  for (let i = 0; i < lines.length; i += 1) {
    const trimmed = lines[i].trim();

    // Skip empty lines, the error message line, and [native code] frames (Safari)
    if ((!trimmed) || trimmed.includes('[native code]')) continue;

    // Skip frames from libraries
    if (shouldSkipFrame(trimmed)) continue;

    // Try V8 format first (Chrome/Edge), then Gecko format (Firefox/Safari)
    const parsed = parseV8Frame(trimmed) ?? parseGeckoFrame(trimmed);
    if (!parsed) continue;

    // Skip if the resolved file matches skip patterns
    if (shouldSkipFrame(parsed.file)) continue;

    const filename = extractFilename(parsed.file);
    return `${errorType}@${filename}:${parsed.line}:${parsed.col}`;
  }

  return `${errorType}@unknown`;
};

export default deriveErrorCode;
